import { z } from "zod";
import { questions } from "./questions";
import { modes, services, verticals } from "./types";

const answerValue = z.union([
  z.string().max(12_000),
  z.boolean(),
  z.array(z.string().max(220)).max(30),
]);

export const contactSchema = z
  .object({
    name: z.string().trim().min(1).max(180),
    email: z.string().trim().email().max(320),
    phone: z.string().trim().max(80).optional(),
    company: z.string().trim().max(220).optional(),
  })
  .strict();

export const serverDraftSchema = z
  .object({
    draftId: z.string().uuid(),
    schemaVersion: z.string().min(1).max(80),
    mode: z.literal("private"),
    vertical: z.enum(verticals),
    service: z.enum(services),
    answers: z.record(z.string(), answerValue),
    contact: contactSchema,
    startedAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    currentChapter: z.number().int().min(0).max(500),
  })
  .strict();

export const submissionRequestSchema = z
  .object({
    content: z
      .object({
        submissionId: z.string().uuid(),
        revision: z.number().int().min(1).max(1_000),
        schemaVersion: z.string().min(1).max(80),
        mode: z.enum(modes),
        vertical: z.enum(verticals),
        service: z.enum(services),
        answers: z.record(z.string(), answerValue),
        startedAt: z.string().datetime(),
        contact: contactSchema,
      })
      .strict(),
    payloadHash: z.string().regex(/^[a-f0-9]{64}$/),
    consent: z.literal(true),
    website: z.string().max(0),
  })
  .strict();

export type SubmissionRequest = z.infer<typeof submissionRequestSchema>;

const knownIds = new Set(questions.map((question) => question.id));
const privateIds = new Set(
  questions.filter((question) => question.modes?.includes("private")).map((question) => question.id),
);

export function validateAnswerAuthority(request: SubmissionRequest): string[] {
  const issues: string[] = [];
  for (const id of Object.keys(request.content.answers)) {
    if (!knownIds.has(id)) issues.push(`Unexpected answer field: ${id}`);
    if (request.content.mode === "public" && privateIds.has(id)) {
      issues.push(`Private answer cannot enter public submission: ${id}`);
    }
  }
  return issues;
}
